import { Check, ChevronDown, ChevronRight, Copy, Download, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin/admin-layout";
import { usePageTitle } from "@/hooks/use-page-title";
import { cn } from "@/lib/utils";

interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
}

function CodeBlock({ code, language = "json", title }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-border bg-zinc-950">
      {title && (
        <div className="px-4 py-2 bg-zinc-900 border-b border-border flex items-center justify-between">
          <span className="text-xs font-medium text-zinc-400">{title}</span>
          <button
            onClick={copyToClipboard}
            className="p-1 rounded hover:bg-zinc-800 transition-colors"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-zinc-500" />
            )}
          </button>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm">
        <code className={`language-${language} text-zinc-300`}>{code}</code>
      </pre>
    </div>
  );
}

interface EndpointProps {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  parameters?: { name: string; type: string; description: string; required?: boolean }[];
  queryParams?: { name: string; type: string; description: string; default?: string }[];
  responseExample: string;
  children?: React.ReactNode;
}

function Endpoint({
  method,
  path,
  description,
  parameters,
  queryParams,
  responseExample,
  children,
}: EndpointProps) {
  const [isOpen, setIsOpen] = useState(false);

  const methodColors = {
    GET: "bg-green-500/10 text-green-500 border-green-500/20",
    POST: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    PUT: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    DELETE: "bg-red-500/10 text-red-500 border-red-500/20",
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <span
          className={cn(
            "px-2 py-0.5 text-xs font-bold rounded border shrink-0",
            methodColors[method]
          )}
        >
          {method}
        </span>
        <code className="text-sm font-mono text-foreground">{path}</code>
        <span className="text-sm text-muted-foreground ml-auto hidden sm:block">{description}</span>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-border bg-secondary/30 space-y-4">
          <p className="text-sm text-muted-foreground sm:hidden">{description}</p>

          {parameters && parameters.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Path Parameters</h4>
              <div className="space-y-2">
                {parameters.map((param) => (
                  <div key={param.name} className="flex gap-2 text-sm">
                    <code className="px-1.5 py-0.5 bg-secondary rounded text-xs">{param.name}</code>
                    <span className="text-muted-foreground text-xs">({param.type})</span>
                    {param.required && <span className="text-red-500 text-xs">required</span>}
                    <span className="text-muted-foreground">— {param.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {queryParams && queryParams.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Query Parameters</h4>
              <div className="space-y-2">
                {queryParams.map((param) => (
                  <div key={param.name} className="flex flex-wrap gap-2 text-sm">
                    <code className="px-1.5 py-0.5 bg-secondary rounded text-xs">{param.name}</code>
                    <span className="text-muted-foreground text-xs">({param.type})</span>
                    {param.default && (
                      <span className="text-muted-foreground text-xs">
                        default: {param.default}
                      </span>
                    )}
                    <span className="text-muted-foreground">— {param.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {children}

          <div>
            <h4 className="text-sm font-semibold mb-2">Response Example</h4>
            <CodeBlock code={responseExample} language="json" />
          </div>
        </div>
      )}
    </div>
  );
}

function generateMarkdownGuide(baseUrl: string): string {
  return `# CMS API Documentation

Use these public API endpoints to fetch content from your CMS for external websites, mobile apps, or other applications.

## Base URL

\`\`\`
${baseUrl}/api/cms
\`\`\`

## Quick Start

### Fetch all blog posts

\`\`\`javascript
// JavaScript / TypeScript
const response = await fetch('${baseUrl}/api/cms/posts?type=blog');
const { data, pagination } = await response.json();

// data contains array of posts
// pagination contains { page, limit, total, totalPages, hasMore }
\`\`\`

### Fetch a single post by slug

\`\`\`javascript
const response = await fetch('${baseUrl}/api/cms/posts/my-post-slug');
const { data } = await response.json();

// data.content contains the HTML content
// data.title, data.excerpt, data.featuredImage, etc.
\`\`\`

## Endpoints

### GET /posts

**Description:** List all published posts

**Query Parameters:**
- \`type\` (string): 'blog' or 'case_study'
- \`category\` (string): Filter by category slug
- \`page\` (number, default: 1): Page number for pagination
- \`limit\` (number, default: 10): Items per page (max 50)

**Response Example:**

\`\`\`json
{
  "data": [
    {
      "id": "abc123",
      "type": "blog",
      "title": "My First Post",
      "slug": "my-first-post",
      "excerpt": "A brief summary...",
      "featuredImage": "https://...",
      "author": {
        "name": "John Doe",
        "avatar": "https://..."
      },
      "categories": ["Technology", "Tutorial"],
      "seo": {
        "title": "My First Post | Blog",
        "description": "SEO description...",
        "keywords": "blog, technology"
      },
      "publishedAt": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 10,
    "total": 25,
    "totalPages": 3,
    "hasMore": true
  }
}
\`\`\`

> **Note:** Case studies include additional fields: \`client\`, \`services\`, \`testimonial\`, and \`comparison\`. See the single post endpoint below for the full structure.

### GET /posts/:slug

**Description:** Get a single post by slug

**Path Parameters:**
- \`slug\` (string, required): The URL slug of the post

**Response Example (Blog Post):**

\`\`\`json
{
  "data": {
    "id": "abc123",
    "type": "blog",
    "title": "My First Post",
    "slug": "my-first-post",
    "content": "<p>Full HTML content...</p>",
    "excerpt": "A brief summary...",
    "featuredImage": "https://...",
    "author": {
      "name": "John Doe",
      "avatar": "https://..."
    },
    "categories": ["Technology"],
    "seo": {
      "title": "My First Post | Blog",
      "description": "SEO description...",
      "keywords": "blog, technology"
    },
    "createdAt": "2024-01-10T08:00:00Z",
    "publishedAt": "2024-01-15T10:30:00Z"
  }
}
\`\`\`

**Response Example (Case Study):**

Case studies include additional fields for client information, services, testimonials, and before/after comparisons:

\`\`\`json
{
  "data": {
    "id": "xyz789",
    "type": "case_study",
    "title": "How Acme Corp Scaled Their Platform",
    "slug": "acme-corp-case-study",
    "content": "<p>Full HTML content...</p>",
    "excerpt": "A brief summary...",
    "featuredImage": "https://...",
    "author": {
      "name": "Jane Smith",
      "avatar": "https://..."
    },
    "categories": ["E-commerce", "Platform"],
    "seo": {
      "title": "Acme Corp Case Study",
      "description": "SEO description...",
      "keywords": "case study, e-commerce"
    },
    "client": {
      "name": "Acme Corp",
      "logo": "https://...",
      "industry": "E-commerce"
    },
    "services": ["Platform Build", "API Integration", "Consulting"],
    "testimonial": {
      "quote": "Working with them transformed our business...",
      "author": "Sarah Chen",
      "role": "CTO"
    },
    "comparison": {
      "before": "https://...",
      "after": "https://..."
    },
    "createdAt": "2024-01-10T08:00:00Z",
    "publishedAt": "2024-01-15T10:30:00Z"
  }
}
\`\`\`

### GET /posts/:slug/related

**Description:** Get related posts based on categories

**Path Parameters:**
- \`slug\` (string, required): The URL slug of the current post

**Query Parameters:**
- \`limit\` (number, default: 4): Number of related posts (max 10)

**Response Example:**

\`\`\`json
{
  "data": [
    {
      "id": "def456",
      "type": "blog",
      "title": "Related Post",
      "slug": "related-post",
      "excerpt": "Another great article...",
      "featuredImage": "https://...",
      "author": {
        "name": "Jane Smith",
        "avatar": "https://..."
      },
      "categories": ["Technology"],
      "publishedAt": "2024-01-12T14:00:00Z"
    }
  ]
}
\`\`\`

### GET /categories

**Description:** List all categories

**Query Parameters:**
- \`type\` (string): Filter by type: 'blog', 'case_study', or 'all'

**Response Example:**

\`\`\`json
{
  "data": [
    {
      "id": "cat123",
      "name": "Technology",
      "slug": "technology",
      "type": "all",
      "postCount": 12
    },
    {
      "id": "cat456",
      "name": "Design",
      "slug": "design",
      "type": "blog",
      "postCount": 8
    }
  ]
}
\`\`\`

## Usage Examples

### React / Next.js

\`\`\`typescript
import { useEffect, useState } from 'react';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage: string;
  publishedAt: string;
}

export function usePosts(type: 'blog' | 'case_study' = 'blog') {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(\`${baseUrl}/api/cms/posts?type=\${type}\`)
      .then(res => res.json())
      .then(({ data }) => {
        setPosts(data);
        setLoading(false);
      });
  }, [type]);

  return { posts, loading };
}
\`\`\`

### Vanilla JavaScript

\`\`\`javascript
// Fetch and display blog posts
async function loadBlogPosts() {
  const container = document.getElementById('posts');
  
  const response = await fetch('${baseUrl}/api/cms/posts?type=blog&limit=6');
  const { data: posts } = await response.json();
  
  container.innerHTML = posts.map(post => \`
    <article class="post-card">
      <img src="\${post.featuredImage}" alt="\${post.title}">
      <h2>\${post.title}</h2>
      <p>\${post.excerpt}</p>
      <a href="/blog/\${post.slug}">Read more</a>
    </article>
  \`).join('');
}

loadBlogPosts();
\`\`\`

### cURL

\`\`\`bash
# Get all blog posts
curl "${baseUrl}/api/cms/posts?type=blog"

# Get a specific post
curl "${baseUrl}/api/cms/posts/my-post-slug"

# Get case studies with pagination
curl "${baseUrl}/api/cms/posts?type=case_study&page=2&limit=5"

# Get posts by category
curl "${baseUrl}/api/cms/posts?category=technology"
\`\`\`

## Tips

- The \`content\` field contains raw HTML. Use \`dangerouslySetInnerHTML\` in React or \`v-html\` in Vue to render it.
- Only **published** posts are returned by the public API. Draft posts are hidden.
- Use the \`seo\` object to populate meta tags for better SEO.
- Images and videos are served as URLs. For locally uploaded media under 1MB, they're base64 encoded.

---

Generated on ${new Date().toLocaleString()}
`;
}

export function ApiGuide() {
  usePageTitle("API Guide");

  const baseUrl = window.location.origin;
  const [copied, setCopied] = useState(false);

  const copyAsMarkdown = () => {
    const markdown = generateMarkdownGuide(baseUrl);
    navigator.clipboard.writeText(markdown);
    setCopied(true);
    toast.success("API guide copied as markdown!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">API Documentation</h1>
            <p className="text-muted-foreground">
              Use these public API endpoints to fetch content from your CMS for external websites,
              mobile apps, or other applications.
            </p>
          </div>
          <button
            onClick={copyAsMarkdown}
            className="mt-2 px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
            title="Copy entire guide as markdown for AI agents"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Copy as Markdown
              </>
            )}
          </button>
        </div>

        {/* Base URL */}
        <div className="mb-8 p-4 bg-secondary/50 rounded-lg border border-border">
          <h2 className="text-sm font-semibold text-muted-foreground mb-2">Base URL</h2>
          <div className="flex items-center gap-2">
            <code className="text-lg font-mono text-foreground">{baseUrl}/api/cms</code>
            <button
              onClick={() => navigator.clipboard.writeText(`${baseUrl}/api/cms`)}
              className="p-1.5 rounded hover:bg-secondary transition-colors"
              title="Copy base URL"
            >
              <Copy className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Quick Start */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Quick Start</h2>
          <div className="space-y-4">
            <CodeBlock
              title="Fetch all blog posts"
              language="javascript"
              code={`// JavaScript / TypeScript
const response = await fetch('${baseUrl}/api/cms/posts?type=blog');
const { data, pagination } = await response.json();

// data contains array of posts
// pagination contains { page, limit, total, totalPages, hasMore }`}
            />

            <CodeBlock
              title="Fetch a single post by slug"
              language="javascript"
              code={`const response = await fetch('${baseUrl}/api/cms/posts/my-post-slug');
const { data } = await response.json();

// data.content contains the HTML content
// data.title, data.excerpt, data.featuredImage, etc.`}
            />
          </div>
        </div>

        {/* Endpoints */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Endpoints</h2>

          <div className="space-y-3">
            {/* List Posts */}
            <Endpoint
              method="GET"
              path="/posts"
              description="List all published posts"
              queryParams={[
                {
                  name: "type",
                  type: "string",
                  description: "'blog' or 'case_study'",
                },
                {
                  name: "category",
                  type: "string",
                  description: "Filter by category slug",
                },
                {
                  name: "page",
                  type: "number",
                  default: "1",
                  description: "Page number for pagination",
                },
                {
                  name: "limit",
                  type: "number",
                  default: "10",
                  description: "Items per page (max 50)",
                },
              ]}
              responseExample={`{
  "data": [
    {
      "id": "abc123",
      "type": "blog",
      "title": "My First Post",
      "slug": "my-first-post",
      "excerpt": "A brief summary...",
      "featuredImage": "https://...",
      "author": {
        "name": "John Doe",
        "avatar": "https://..."
      },
      "categories": ["Technology", "Tutorial"],
      "seo": {
        "title": "My First Post | Blog",
        "description": "SEO description...",
        "keywords": "blog, technology"
      },
      "publishedAt": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 10,
    "total": 25,
    "totalPages": 3,
    "hasMore": true
  }
}`}
            />

            {/* Get Single Post */}
            <Endpoint
              method="GET"
              path="/posts/:slug"
              description="Get a single post by slug"
              parameters={[
                {
                  name: "slug",
                  type: "string",
                  description: "The URL slug of the post",
                  required: true,
                },
              ]}
              responseExample={`{
  "data": {
    "id": "abc123",
    "type": "blog",
    "title": "My First Post",
    "slug": "my-first-post",
    "content": "<p>Full HTML content...</p>",
    "excerpt": "A brief summary...",
    "featuredImage": "https://...",
    "author": {
      "name": "John Doe",
      "avatar": "https://..."
    },
    "categories": ["Technology"],
    "seo": {
      "title": "My First Post | Blog",
      "description": "SEO description...",
      "keywords": "blog, technology"
    },
    "createdAt": "2024-01-10T08:00:00Z",
    "publishedAt": "2024-01-15T10:30:00Z"
  }
}`}
            >
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
                <p className="font-medium text-amber-400 mb-1">Case Study Additional Fields</p>
                <p className="text-muted-foreground text-xs">
                  Case studies include extra fields: <code className="text-foreground">client</code>{" "}
                  (name, logo, industry), <code className="text-foreground">services</code> (array),{" "}
                  <code className="text-foreground">testimonial</code> (quote, author, role), and{" "}
                  <code className="text-foreground">comparison</code> (before/after images).
                </p>
              </div>
            </Endpoint>

            {/* Related Posts */}
            <Endpoint
              method="GET"
              path="/posts/:slug/related"
              description="Get related posts based on categories"
              parameters={[
                {
                  name: "slug",
                  type: "string",
                  description: "The URL slug of the current post",
                  required: true,
                },
              ]}
              queryParams={[
                {
                  name: "limit",
                  type: "number",
                  default: "4",
                  description: "Number of related posts (max 10)",
                },
              ]}
              responseExample={`{
  "data": [
    {
      "id": "def456",
      "type": "blog",
      "title": "Related Post",
      "slug": "related-post",
      "excerpt": "Another great article...",
      "featuredImage": "https://...",
      "author": {
        "name": "Jane Smith",
        "avatar": "https://..."
      },
      "categories": ["Technology"],
      "publishedAt": "2024-01-12T14:00:00Z"
    }
  ]
}`}
            />

            {/* Categories */}
            <Endpoint
              method="GET"
              path="/categories"
              description="List all categories"
              queryParams={[
                {
                  name: "type",
                  type: "string",
                  description: "Filter by type: 'blog', 'case_study', or 'all'",
                },
              ]}
              responseExample={`{
  "data": [
    {
      "id": "cat123",
      "name": "Technology",
      "slug": "technology",
      "type": "all",
      "postCount": 12
    },
    {
      "id": "cat456",
      "name": "Design",
      "slug": "design",
      "type": "blog",
      "postCount": 8
    }
  ]
}`}
            />
          </div>
        </div>

        {/* Case Study Fields */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Case Study Fields</h2>
          <p className="text-muted-foreground text-sm mb-4">
            Case studies (<code className="text-foreground">type: "case_study"</code>) include
            additional fields for showcasing client work:
          </p>

          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/50 border-b border-border">
                  <th className="px-4 py-2 text-left font-semibold">Field</th>
                  <th className="px-4 py-2 text-left font-semibold">Type</th>
                  <th className="px-4 py-2 text-left font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="px-4 py-2">
                    <code>client</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">object | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Client info with <code>name</code>, <code>logo</code>, <code>industry</code>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>services</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">string[]</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Array of services/products used in the project
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>testimonial</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">object | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Client testimonial with <code>quote</code>, <code>author</code>,{" "}
                    <code>role</code>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>comparison</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">object | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Before/after images with <code>before</code> and <code>after</code> URLs
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>projectTemplateId</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">string | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Optional project template ID to link the case study to a template
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>liveUrl</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">string | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    URL to the live website for this case study
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2">
                    <code>prompt</code>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">string | null</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    Starting prompt for the "Build it" card on case study pages
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <CodeBlock
            title="Case Study Response Example"
            code={`{
  "data": {
    "id": "xyz789",
    "type": "case_study",
    "title": "How Acme Corp Scaled Their Platform",
    "client": {
      "name": "Acme Corp",
      "logo": "https://...",
      "industry": "E-commerce"
    },
    "services": ["Platform Build", "API Integration"],
    "testimonial": {
      "quote": "They transformed our business completely.",
      "author": "Sarah Chen",
      "role": "CTO"
    },
    "comparison": {
      "before": "https://...",
      "after": "https://..."
    },
    "projectTemplateId": "tmpl_abc123",
    "liveUrl": "https://acme-platform.example.com",
    "prompt": "Create a similar e-commerce platform for your business",
    // ... other standard fields
  }
}`}
          />
        </div>

        {/* Usage Examples */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Usage Examples</h2>

          <div className="space-y-6">
            {/* React Example */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-500/10 text-blue-500 flex items-center justify-center text-xs font-bold">
                  R
                </span>
                React / Next.js
              </h3>
              <CodeBlock
                title="usePosts.ts"
                language="typescript"
                code={`import { useEffect, useState } from 'react';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage: string;
  publishedAt: string;
}

export function usePosts(type: 'blog' | 'case_study' = 'blog') {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(\`${baseUrl}/api/cms/posts?type=\${type}\`)
      .then(res => res.json())
      .then(({ data }) => {
        setPosts(data);
        setLoading(false);
      });
  }, [type]);

  return { posts, loading };
}`}
              />
            </div>

            {/* Vanilla JS Example */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-amber-500/10 text-amber-500 flex items-center justify-center text-xs font-bold">
                  JS
                </span>
                Vanilla JavaScript
              </h3>
              <CodeBlock
                title="blog.js"
                language="javascript"
                code={`// Fetch and display blog posts
async function loadBlogPosts() {
  const container = document.getElementById('posts');
  
  const response = await fetch('${baseUrl}/api/cms/posts?type=blog&limit=6');
  const { data: posts } = await response.json();
  
  container.innerHTML = posts.map(post => \`
    <article class="post-card">
      <img src="\${post.featuredImage}" alt="\${post.title}">
      <h2>\${post.title}</h2>
      <p>\${post.excerpt}</p>
      <a href="/blog/\${post.slug}">Read more</a>
    </article>
  \`).join('');
}

loadBlogPosts();`}
              />
            </div>

            {/* cURL Example */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-green-500/10 text-green-500 flex items-center justify-center text-xs font-bold">
                  $
                </span>
                cURL
              </h3>
              <CodeBlock
                title="Terminal"
                language="bash"
                code={`# Get all blog posts
curl "${baseUrl}/api/cms/posts?type=blog"

# Get a specific post
curl "${baseUrl}/api/cms/posts/my-post-slug"

# Get case studies with pagination
curl "${baseUrl}/api/cms/posts?type=case_study&page=2&limit=5"

# Get posts by category
curl "${baseUrl}/api/cms/posts?category=technology"`}
              />
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="font-semibold text-blue-400 mb-2">💡 Tips</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span>•</span>
              <span>
                The <code className="text-foreground">content</code> field contains raw HTML. Use{" "}
                <code className="text-foreground">dangerouslySetInnerHTML</code> in React or{" "}
                <code className="text-foreground">v-html</code> in Vue to render it.
              </span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>
                Only <strong>published</strong> posts are returned by the public API. Draft posts
                are hidden.
              </span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>
                Use the <code className="text-foreground">seo</code> object to populate meta tags
                for better SEO.
              </span>
            </li>
            <li className="flex gap-2">
              <span>•</span>
              <span>
                Images and videos are served as URLs. For locally uploaded media under 1MB, they're
                base64 encoded.
              </span>
            </li>
          </ul>
        </div>

        {/* Test Link */}
        <div className="mt-8 text-center">
          <a
            href={`${baseUrl}/api/cms/posts`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Open API in new tab to test
          </a>
        </div>
      </div>
    </AdminLayout>
  );
}
